import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { CheckCircle2, Loader2, ArrowLeftRight, BadgePercent } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { fetchVehicles, submitTradeIn, formatApiErrorDetail } from "@/api";

const CONDITIONS = ["excellent", "good", "fair", "needs work"];

export default function TradeIn() {
  const [vehicles, setVehicles] = useState([]);
  const [form, setForm] = useState({
    full_name: "", email: "", mobile: "",
    current_make: "", current_model: "", current_year: "", mileage_km: "",
    condition: "good", desired_vehicle: "", message: "",
  });
  const [loading, setLoading] = useState(false);
  const [reference, setReference] = useState(null);

  useEffect(() => {
    fetchVehicles().then(setVehicles).catch(() => {});
  }, []);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await submitTradeIn({
        ...form,
        current_year: Number(form.current_year),
        mileage_km: Number(form.mileage_km || 0),
      });
      setReference(res.reference);
      toast.success("Trade-in enquiry sent");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="tradein-page" className="pt-32 pb-24 min-h-screen">
      <div className="w-full max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-2 gap-16">
        <Reveal>
          <p className="text-xs uppercase tracking-[0.3em] text-sky-300 font-bold mb-4">Trade-in</p>
          <h1 className="font-display text-4xl sm:text-5xl tracking-tight font-black text-white mb-6">
            Your current car counts.
          </h1>
          <p className="text-base text-slate-400 leading-relaxed mb-10">
            Tell us what you're driving now and what you'd like from our floor.
            We'll assess your vehicle fairly and put its value towards your
            CompCar deal — a rental, a rent-to-own plan or an outright purchase.
          </p>
          <ul className="space-y-5">
            {[
              "Fair, transparent assessment — no lowball games",
              "Trade-in value can cover your ownership deposit",
              "Works with rentals, rent-to-own and sales",
            ].map((t) => (
              <li key={t} className="flex items-start gap-3 text-sm text-slate-300">
                <BadgePercent size={18} className="text-[#F57C00] mt-0.5 shrink-0" />
                {t}
              </li>
            ))}
          </ul>
        </Reveal>

        <Reveal delay={0.15}>
          <AnimatePresence mode="wait">
            {!reference ? (
              <motion.form
                key="form"
                data-testid="tradein-form"
                onSubmit={submit}
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -24 }}
                transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
                className="bg-[#122659]/80 backdrop-blur-xl border border-sky-300/20 rounded-xl p-8 md:p-10 space-y-5"
              >
                <p className="text-xs uppercase tracking-[0.2em] text-sky-300 font-bold flex items-center gap-2">
                  <ArrowLeftRight size={14} className="text-[#F57C00]" /> Your current vehicle
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <label className="input-label" htmlFor="ti-make">Make</label>
                    <input id="ti-make" data-testid="tradein-input-make" required className="input-field" value={form.current_make} onChange={set("current_make")} placeholder="Toyota" />
                  </div>
                  <div>
                    <label className="input-label" htmlFor="ti-model">Model</label>
                    <input id="ti-model" data-testid="tradein-input-model" required className="input-field" value={form.current_model} onChange={set("current_model")} placeholder="Etios 1.5" />
                  </div>
                  <div>
                    <label className="input-label" htmlFor="ti-year">Year</label>
                    <input id="ti-year" data-testid="tradein-input-year" type="number" required min="1990" max="2026" className="input-field" value={form.current_year} onChange={set("current_year")} placeholder="2018" />
                  </div>
                  <div>
                    <label className="input-label" htmlFor="ti-km">Mileage (km)</label>
                    <input id="ti-km" data-testid="tradein-input-km" type="number" min="0" className="input-field" value={form.mileage_km} onChange={set("mileage_km")} placeholder="120000" />
                  </div>
                  <div className="sm:col-span-2">
                    <label className="input-label" htmlFor="ti-cond">Condition</label>
                    <select id="ti-cond" data-testid="tradein-select-condition" className="input-field" value={form.condition} onChange={set("condition")}>
                      {CONDITIONS.map((c) => <option key={c} value={c}>{c[0].toUpperCase() + c.slice(1)}</option>)}
                    </select>
                  </div>
                </div>

                <p className="text-xs uppercase tracking-[0.2em] text-sky-300 font-bold pt-2">What you'd like</p>
                <div>
                  <label className="input-label" htmlFor="ti-want">CompCar vehicle (optional)</label>
                  <select id="ti-want" data-testid="tradein-select-desired" className="input-field" value={form.desired_vehicle} onChange={set("desired_vehicle")}>
                    <option value="">Any / advise me</option>
                    {vehicles.map((v) => (
                      <option key={v.slug} value={`${v.year} ${v.make} ${v.model}`}>{v.year} {v.make} {v.model}</option>
                    ))}
                  </select>
                </div>

                <p className="text-xs uppercase tracking-[0.2em] text-sky-300 font-bold pt-2">Your details</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div className="sm:col-span-2">
                    <label className="input-label" htmlFor="ti-name">Full name</label>
                    <input id="ti-name" data-testid="tradein-input-name" required className="input-field" value={form.full_name} onChange={set("full_name")} placeholder="Thandi Mokoena" />
                  </div>
                  <div>
                    <label className="input-label" htmlFor="ti-email">Email</label>
                    <input id="ti-email" data-testid="tradein-input-email" type="email" required className="input-field" value={form.email} onChange={set("email")} placeholder="you@example.co.za" />
                  </div>
                  <div>
                    <label className="input-label" htmlFor="ti-mobile">Mobile</label>
                    <input id="ti-mobile" data-testid="tradein-input-mobile" required className="input-field" value={form.mobile} onChange={set("mobile")} placeholder="082 000 0000" />
                  </div>
                  <div className="sm:col-span-2">
                    <label className="input-label" htmlFor="ti-msg">Anything else we should know?</label>
                    <textarea id="ti-msg" data-testid="tradein-input-message" rows={3} className="input-field resize-none" value={form.message} onChange={set("message")} placeholder="Service history, accident record, outstanding finance…" />
                  </div>
                </div>

                <button type="submit" data-testid="tradein-submit-btn" disabled={loading} className="w-full bg-[#F57C00] hover:bg-[#E67300] disabled:opacity-60 text-white font-bold px-6 py-4 rounded-md transition-colors duration-300 flex items-center justify-center gap-2">
                  {loading && <Loader2 size={18} className="animate-spin" />}
                  {loading ? "Sending…" : "Get my trade-in value"}
                </button>
              </motion.form>
            ) : (
              <motion.div
                key="done"
                data-testid="tradein-success"
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-[#122659]/80 backdrop-blur-xl border border-emerald-400/40 rounded-xl p-8 md:p-10 text-center"
              >
                <CheckCircle2 size={56} className="text-emerald-400 mx-auto mb-6" />
                <h2 className="font-display text-3xl font-black text-white mb-3">Enquiry received.</h2>
                <p className="text-slate-400 text-sm mb-2">Your reference is</p>
                <p className="font-display text-2xl font-black text-[#F57C00] tracking-widest mb-8" data-testid="tradein-reference">{reference}</p>
                <p className="text-xs text-slate-500 mb-8 leading-relaxed">
                  A confirmation is on its way to your email. Our team will assess your
                  vehicle and come back with a fair value towards your CompCar deal.
                </p>
                <Link to="/vehicles" data-testid="tradein-browse" className="text-sky-300 hover:text-white font-bold text-sm transition-colors">
                  ← Browse vehicles meanwhile
                </Link>
              </motion.div>
            )}
          </AnimatePresence>
        </Reveal>
      </div>
    </div>
  );
}
